import { IconBellRinging, IconX } from "@tabler/icons-react";
import { useLocation } from "react-router-dom";
import { useSiteNotifications } from "@common/features/User";
import { __ } from "@common/helpers/i18n";
import { Button } from "@app/elements/form";
import { ForwardIcon } from "@app/elements/helpers";
import { sanitizedHtml } from "@app/helpers/sanitizeHtml";
import { useLocalStorage } from "@app/hooks";
import { cn } from "@app/lib/utils";

export default function SiteNotification({
  className,
}: {
  className?: string;
}) {
  const location = useLocation();
  const notifications = useSiteNotifications();

  const [closedNotifications, setClosedNotifications] = useLocalStorage<
    number[]
  >("site-notifications", []);

  const visibleNotifications = notifications.filter(
    (notification) =>
      !closedNotifications.includes(notification.id) &&
      (!notification.url || location.pathname.startsWith(notification.url)),
  );

  if (visibleNotifications.length === 0) {
    return null;
  }

  const notification = visibleNotifications[0];

  return (
    <div className={cn("mx-auto mt-5 w-full max-w-[1440px]", className)}>
      <section
        key={notification.id}
        className="relative overflow-hidden rounded-lg border border-border bg-card shadow-soft"
      >
        <div className="absolute inset-y-0 start-0 w-1 bg-primary/75 dark:bg-foreground/70" />
        <div className="flex min-w-0 flex-col gap-3 py-3 pe-3 ps-5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/[0.08] text-primary ring-1 ring-primary/[0.12] dark:bg-background dark:text-foreground dark:ring-border">
              <IconBellRinging size={19} stroke={2} />
            </span>

            <div
              className="sanitized-html min-w-0 text-start text-sm font-semibold leading-7 text-foreground [&_a]:font-bold [&_a]:text-primary [&_p]:m-0 [&_strong]:font-extrabold [&_strong]:text-foreground"
              dangerouslySetInnerHTML={sanitizedHtml(notification.content)}
            />
          </div>

          <div className="flex shrink-0 items-center justify-end gap-2">
            {notification.button && (
              <Button
                variant="outline-light"
                href={notification.button_url!}
                padding="small"
                className="h-10 border-primary/20 bg-primary/5 px-3 text-primary shadow-none hover:bg-primary/10 dark:border-border dark:bg-background dark:text-foreground dark:hover:bg-muted dark:hover:text-foreground"
                icon={<ForwardIcon size={16} stroke={2} />}
                iconPosition="end"
              >
                {notification.button}
              </Button>
            )}
            {notification.closable === 1 && (
              <button
                type="button"
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-transparent text-muted-foreground transition-all hover:border-border hover:bg-card hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/20"
                aria-label={__("Close notification")}
                onClick={() => {
                  setClosedNotifications((closedNotifications) => [
                    ...new Set([...closedNotifications, notification.id]),
                  ]);
                }}
              >
                <IconX size={18} stroke={2} />
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
